import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import type { BlacklistCheckResult } from '../services/customerBlacklistService';

/** Kara liste rengi: iki temada da siyah bant + beyaz yazı. */
export const BLACKLIST_BLACK = '#1a1a1a';

const fmtDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('tr-TR');
};

interface BlacklistBannerProps {
  result: BlacklistCheckResult | null | undefined;
  /** Otomatik onay durduruldu satırı (yeni sipariş penceresi) */
  showAutoPaused?: boolean;
  onOpenEntry?: (entryId: number) => void;
  dense?: boolean;
}

/**
 * Kara listedeki müşteri bandı (siyah) ya da olası eşleşme notu (sarı).
 * Yeni sipariş penceresi, gelen arama ve soldaki müşteri kartında kullanılır.
 */
const BlacklistBanner: React.FC<BlacklistBannerProps> = ({ result, showAutoPaused, onOpenEntry, dense }) => {
  const { t } = useTranslation('salesSystem');
  const k = (key: string, opts?: Record<string, unknown>) => t(`orderManagement.blacklist.${key}`, opts as any) as string;

  const match = result?.match;
  if (match) {
    const keys = match.matchedBy.map(m => k(`matchedBy.${m}`)).join(' + ');
    const branch = match.ownerBranchName ? `${match.ownerBranchName} · ` : '';
    const by = match.createdByName || '—';
    return (
      <Box sx={{ bgcolor: BLACKLIST_BLACK, color: '#fff', borderRadius: 1.5, p: dense ? 1.25 : 1.75, display: 'grid', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <BlockIcon sx={{ fontSize: dense ? 18 : 22 }} />
          <Typography fontWeight={800} fontSize={dense ? 13 : 15} letterSpacing=".02em">{k('banner.title')}</Typography>
          {onOpenEntry && (
            <Button size="small" onClick={() => onOpenEntry(match.entryId)}
              sx={{ ml: 'auto', color: '#fff', textDecoration: 'underline', minWidth: 0, p: 0, fontSize: 12 }}>
              {k('banner.openEntry')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Box component="span" sx={{ bgcolor: 'rgba(255,255,255,.16)', borderRadius: 99, px: 1, py: 0.25, fontSize: 12, fontWeight: 600 }}>
            {k(`reason.${match.reason}`)}
          </Box>
          {keys && (
            <Box component="span" sx={{ bgcolor: 'rgba(255,255,255,.16)', borderRadius: 99, px: 1, py: 0.25, fontSize: 12 }}>
              {k('banner.matchedBy', { keys })}
            </Box>
          )}
        </Box>
        <Typography fontSize={12.5} sx={{ opacity: 0.85 }}>
          {k('banner.addedBy', { date: fmtDate(match.createdAt), branch, by })}
          {match.note ? ` · “${match.note}”` : ''}
        </Typography>
        {(match.hitCount > 0 || match.lastAction) && !dense && (
          <Typography fontSize={12.5} sx={{ opacity: 0.85 }}>
            {match.hitCount > 0 ? k('banner.hitInfo', { count: match.hitCount }) : ''}
            {match.hitCount > 0 && match.lastAction ? ' · ' : ''}
            {match.lastAction ? k('banner.lastDecision', { action: k(`action.${match.lastAction}`), date: fmtDate(match.lastActionAt) }) : ''}
          </Typography>
        )}
        {showAutoPaused && (
          <Typography fontSize={12.5} fontWeight={700} sx={{ color: '#ff8a80' }}>{k('banner.autoPaused')}</Typography>
        )}
      </Box>
    );
  }

  const possible = result?.possible;
  if (possible) {
    return (
      <Box sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', borderRadius: 1.5, p: dense ? 1 : 1.25, display: 'grid', gap: 0.25 }}>
        <Typography fontWeight={700} fontSize={13}>{k('banner.possibleTitle')} · {k(`reason.${possible.reason}`)}</Typography>
        <Typography fontSize={12.5}>{k('banner.possibleBody', { name: possible.displayName })}</Typography>
      </Box>
    );
  }
  return null;
};

export default BlacklistBanner;
